import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import MyDialogContent from "./MyDialogContent";

export const INTERNAL_NOTE_SUBJECT_MAX_CHARS = 50;
export const INTERNAL_NOTE_CONTENT_MAX_CHARS = 1500;

export type InternalNoteRecord = {
  _id: string;
  content: string;
  subject?: string;
  [key: string]: unknown;
};

export type InternalNoteSaveResult =
  | { type: "created"; apiNote: unknown | null }
  | {
      type: "updated";
      noteId: string;
      subject: string;
      content: string;
    };

export type PatientInternalNoteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string | undefined;
  noteToEdit: InternalNoteRecord | null;
  authorName: string;
  canManageNote: (note: InternalNoteRecord) => boolean;
  onSaved: (result: InternalNoteSaveResult) => void;
  /** When false, hides the dialog corner close button (e.g. force Cancel/submit path). */
  showCloseButton?: boolean;
};

export function PatientInternalNoteDialog({
  open,
  onOpenChange,
  patientId,
  noteToEdit,
  authorName,
  canManageNote,
  onSaved,
  showCloseButton = true,
}: PatientInternalNoteDialogProps) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const isEditing = !!noteToEdit;

  useEffect(() => {
    if (!open) {
      setSubject("");
      setContent("");
      setSubjectError(null);
      setContentError(null);
      return;
    }
    setSubjectError(null);
    setContentError(null);
    if (noteToEdit) {
      setSubject(noteToEdit.subject ?? "");
      setContent(noteToEdit.content ?? "");
    } else {
      setSubject("");
      setContent("");
    }
  }, [open, noteToEdit]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const subjectTrim = subject.trim();
    const contentTrim = content.trim();

    const subjectErr = !subjectTrim
      ? "Subject is required."
      : subjectTrim.length > INTERNAL_NOTE_SUBJECT_MAX_CHARS
        ? `Subject must be no more than ${INTERNAL_NOTE_SUBJECT_MAX_CHARS} characters.`
        : null;
    const contentErr = !contentTrim
      ? "Note content is required."
      : contentTrim.length > INTERNAL_NOTE_CONTENT_MAX_CHARS
        ? `Note content must be no more than ${INTERNAL_NOTE_CONTENT_MAX_CHARS} characters.`
        : null;

    setSubjectError(subjectErr);
    setContentError(contentErr);
    if (subjectErr || contentErr) return;

    if (!patientId) {
      toast.error("Patient is not loaded yet.");
      return;
    }

    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);

    try {
      setSubmitLoading(true);

      if (isEditing && noteToEdit) {
        if (!canManageNote(noteToEdit)) {
          throw new Error("You can only edit your own internal notes");
        }
        await AxiosInstance().put(`/patients/internal-note/${noteToEdit._id}`, {
          content: contentTrim,
          subject: subjectTrim,
        });

        onSaved({
          type: "updated",
          noteId: noteToEdit._id,
          subject: subjectTrim,
          content: contentTrim,
        });
        resolveFn("Internal note updated successfully");
      } else {
        const payload = {
          content: contentTrim,
          subject: subjectTrim,
          patientId,
          authorName,
          createdAt: Date.now(),
        };

        const { data } = await AxiosInstance().post<{ data?: unknown | null }>(
          "/patients/internal-note",
          payload,
        );
        onSaved({ type: "created", apiNote: data?.data ?? null });
        resolveFn("Internal note added successfully");
      }

      onOpenChange(false);
    } catch (error: unknown) {
      rejectFn(error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Operation failed";
      toast.error(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent heading={isEditing ? "Edit Internal Note" : "Add Internal Note"}>
      {/* <DialogContent
        className="w-[50vw] !max-w-none max-h-[50vh] overflow-x-hidden"
        showCloseButton={showCloseButton}
      > */}
        {/* <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Internal Note</DialogTitle>
        </DialogHeader> */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 pr-1 mb-4">
            <div className="space-y-2">
              <Label htmlFor="internal-note-subject">Subject</Label>
              <Textarea
                id="internal-note-subject"
                placeholder="Enter Subject..."
                rows={1}
                maxLength={50}
                value={subject}
                onChange={(ev) => {
                  setSubject(ev.target.value);
                  setSubjectError(null);
                }}
                aria-invalid={!!subjectError}
              />
              <div className="flex justify-end">
              <p
                className={
                  subject.length > INTERNAL_NOTE_SUBJECT_MAX_CHARS
                    ? "text-xs text-destructive"
                    : "text-xs text-muted-foreground"
                }
              >
                {subject.length}/{INTERNAL_NOTE_SUBJECT_MAX_CHARS}
              </p>
              {subjectError ? (
                <p className="text-sm text-destructive" role="alert">
                  {subjectError}
                </p>
              ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="internal-note-content">Note Content</Label>
              <Textarea
                id="internal-note-content"
                placeholder="Enter internal note..."
                rows={6}
                maxLength={1500}
                value={content}
                onChange={(ev) => {
                  setContent(ev.target.value);
                  setContentError(null);
                }}
                aria-invalid={!!contentError}
              />
              <div className="flex justify-end">
              <p
                className={
                  content.length > INTERNAL_NOTE_CONTENT_MAX_CHARS
                    ? "text-xs text-destructive"
                    : "text-xs text-muted-foreground"
                }
              >
                {content.length}/{INTERNAL_NOTE_CONTENT_MAX_CHARS}
              </p>
              {contentError ? (
                <p className="text-sm text-destructive" role="alert">
                  {contentError}
                </p>
              ) : null}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 sticky bottom-0 w-full bg-white">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={submitLoading} type="submit">
              {isEditing ? "Update Note" : "Add Note"}
            </Button>
          </div>
        </form>
      {/* </DialogContent> */}
      </MyDialogContent>
    </Dialog>
  );
}
